import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { CheckCircle2, Target, Users, Wrench } from "lucide-react";
import { GhostButton, PageHero, PrimaryButton, Reveal, SectionTag, TechTag } from "@/components/ui-bits";
import { ENGAGEMENT_MODELS, IMAGES } from "@/content/site";
import api from "@/lib/api";

const ListBlock = ({ icon: Icon, title, items, testId }) => (
  <div className="rounded-2xl border border-tark-line bg-tark-card p-8">
    <div className="flex items-center gap-3">
      <span className="rounded-lg border border-tark-blue/40 bg-tark-blue/10 p-2 text-tark-cyan"><Icon className="h-4 w-4" /></span>
      <h3 className="font-display text-lg font-bold tracking-tight">{title}</h3>
    </div>
    <ul className="mt-6 space-y-3" data-testid={testId}>
      {items.map((item) => (
        <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-tark-mut">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-tark-cyan" />
          {item}
        </li>
      ))}
    </ul>
  </div>
);

const ServiceDetail = () => {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/services/${slug}`)
      .then((r) => setService(r.data))
      .catch(() => setService(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (slug === "data-ai") return <Navigate to="/data-ai" replace />;
  if (loading) return <div className="flex min-h-screen items-center justify-center font-mono text-sm text-tark-dim">Loading…</div>;
  if (!service) return <Navigate to="/services" replace />;

  return (
    <>
      <PageHero
        eyebrow={service.menu_category || "TARK Service"}
        lines={[service.tagline || service.name]}
        sub={service.summary}
        image={IMAGES.cloud}
      >
        <PrimaryButton to="/start-a-project" testId="service-talk-to-expert">Talk to an Expert</PrimaryButton>
        <GhostButton to="/services" testId="service-all-services">All Services</GhostButton>
      </PageHero>

      <section className="mx-auto max-w-7xl px-6 pb-24 lg:px-10" data-testid="service-detail">
        <div className="grid gap-14 lg:grid-cols-5">
          <Reveal className="lg:col-span-3">
            <SectionTag>What TARK Provides</SectionTag>
            <p className="mt-5 text-base leading-relaxed text-tark-mut sm:text-lg" data-testid="service-what">
              {service.what}
            </p>
            {service.technologies?.length > 0 && (
              <div className="mt-10">
                <h3 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-tark-ink">Technologies We Use</h3>
                <div className="mt-5 flex flex-wrap gap-2.5" data-testid="service-technologies">
                  {service.technologies.map((t) => <TechTag key={t}>{t}</TechTag>)}
                </div>
              </div>
            )}
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-2">
            <div className="rounded-2xl border border-tark-line bg-tark-card p-8">
              <h3 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-tark-cyan">Capabilities</h3>
              <ul className="mt-5 space-y-2.5" data-testid="service-capabilities">
                {service.capabilities?.map((c) => (
                  <li key={c} className="border-b border-tark-line/60 pb-2.5 text-sm text-tark-mut last:border-0">{c}</li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>

        <div className="mt-16 grid gap-5 lg:grid-cols-2">
          <Reveal><ListBlock icon={Target} title="Problems We Solve" items={service.problems || []} testId="service-problems" /></Reveal>
          <Reveal delay={0.1}><ListBlock icon={Wrench} title="Typical Solutions Delivered" items={service.solutions || []} testId="service-solutions" /></Reveal>
        </div>

        <div className="mt-5 grid gap-5 lg:grid-cols-2">
          <Reveal><ListBlock icon={Users} title="Who This Service Is For" items={service.audience || []} testId="service-audience" /></Reveal>
          <Reveal delay={0.1}>
            <div className="rounded-2xl border border-tark-line bg-tark-card p-8">
              <h3 className="font-display text-lg font-bold tracking-tight">Engagement Models</h3>
              <ul className="mt-6 space-y-4" data-testid="service-engagement-models">
                {ENGAGEMENT_MODELS.map((m) => (
                  <li key={m.name}>
                    <p className="font-display text-sm font-bold text-tark-ink">{m.name}</p>
                    <p className="mt-1 text-sm text-tark-mut">{m.desc}</p>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>

        <Reveal className="mt-16 rounded-2xl border border-tark-blue/40 bg-gradient-to-br from-tark-card to-[#0A1A33] p-10 sm:p-14">
          <div className="flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
            <div>
              <SectionTag>Next Step</SectionTag>
              <h2 className="mt-3 font-display text-2xl font-bold sm:text-3xl">Discuss Your Project</h2>
              <p className="mt-3 max-w-xl text-sm text-tark-mut">
                Tell us about your environment and objectives. A TARK engineering lead will review your requirements.
              </p>
            </div>
            <div className="flex flex-wrap gap-4">
              <PrimaryButton to="/start-a-project" testId="service-bottom-cta">Request Consultation</PrimaryButton>
              <Link to="/solutions" data-testid="service-solutions-link"
                    className="inline-flex items-center rounded-full border border-tark-line px-7 py-3.5 font-display text-sm font-semibold text-tark-ink transition-colors hover:border-tark-cyan/50 hover:text-tark-cyan">
                Explore Solutions
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </>
  );
};

export default ServiceDetail;
